import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Button } from '@/ui_v3';
import { Layout, PageTitle, Section } from '../shared';
type Focus = 'questions' | 'students' | 'completion';
const FOCUS_ITEMS: Record<Focus, {
  count: string;
}> = {
  questions: {
    count: '2'
  },
  students: {
    count: '4'
  },
  completion: {
    count: '6'
  }
};
export function TeacherDashboardPrototypePageView({
  standalone = false
}: {
  standalone?: boolean;
}) {
  const {
    t
  } = useTranslation(['lms']);
  const [focus, setFocus] = useState<Focus>('questions');
  const [actionComplete, setActionComplete] = useState(false);
  const selectedFocus = FOCUS_ITEMS[focus];
  const focusTitle = (item: Focus) => {
    if (item === 'questions') return t('lms:lms.report.lowCorrectQuestions');
    if (item === 'students') return t('lms:lms.report.lowCorrectStudents');
    return t('lms:lms.studentStats.completionRate');
  };
  const focusDescription = (item: Focus) => {
    if (item === 'questions') {
      return t('lms:lms.report.needMoreGuidanceQuestions');
    }
    if (item === 'students') {
      return t('lms:lms.report.needMoreGuidanceStudents');
    }
    return t('lms:lms.questionStats.status.unanswered');
  };
  const content = <>
      <div className="flex items-start justify-between gap-lg">
        <PageTitle title={t('lms:lms.report.title')} description="Prototype" />
        <span className="mt-xs shrink-0 rounded-full bg-strong-alternative px-sm py-2xs text-caption-md-semibold text-primary">
          Prototype
        </span>
      </div>

      <section className="border-b border-alternative pb-xl">
        <div className="flex items-end justify-between gap-xl">
          <div>
            <p className="text-body-md-medium text-assistive">
              {t('lms:lms.report.totalStats')}
            </p>
            <p className="text-heading-lg-semibold mt-xs text-default">
              {focusDescription(focus)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-display-xs-semibold text-primary">
              {focus === 'completion' ? '76%' : `${selectedFocus.count}`}
            </p>
            <p className="text-body-sm-medium text-assistive">
              {focusTitle(focus)}
            </p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-[minmax(0,1fr)_292px] gap-2xl">
        <div className="flex min-w-0 flex-col gap-2xl">
          <Section.Container>
            <div className="flex items-center justify-between gap-md px-xs">
              <Section.Title>{t('lms:lms.report.totalStats')}</Section.Title>
              <span className="text-body-sm-medium text-assistive">
                {t('lms:lms.studentStats.completionRate')} 76%
              </span>
            </div>

            <div className="mt-md divide-y divide-alternative border-y border-alternative">
              {(['questions', 'students', 'completion'] as Focus[]).map(item => {
              const itemData = FOCUS_ITEMS[item];
              const isSelected = focus === item;
              return <button key={item} type="button" onClick={() => {
                setFocus(item);
                setActionComplete(false);
              }} className="flex w-full items-center justify-between gap-md px-md py-lg text-left transition-colors hover:bg-neutral">
                      <span className="flex items-center gap-md">
                        <span className={isSelected ? 'size-2 rounded-full bg-strong' : 'size-2 rounded-full bg-alternative'} />
                        <span>
                          <span className="block text-body-lg-semibold text-default">
                            {focusTitle(item)}
                          </span>
                          <span className="mt-2xs block text-body-sm-medium text-assistive">
                            {focusDescription(item)}
                          </span>
                        </span>
                      </span>
                      <span className="text-heading-md-semibold text-default">
                        {item === 'completion' ? '76%' : itemData.count}
                      </span>
                    </button>;
            })}
            </div>
          </Section.Container>

          <Section.Container>
            <div className="flex items-center justify-between gap-md px-xs">
              <Section.Title>{t('lms:lms.questionStats.title')}</Section.Title>
              <span className="text-body-sm-medium text-assistive">
                {t('lms:lms.questionStats.lowerstAccuracyRate')}
              </span>
            </div>

            <div className="mt-md grid grid-cols-5 items-end gap-md border-b border-alternative px-md pb-md pt-xl">
              {[82, 68, 41, 58, 76].map((height, index) => <button key={height} type="button" onClick={() => {
              setFocus(index === 2 ? 'questions' : 'students');
              setActionComplete(false);
            }} className="group flex flex-col items-center gap-sm">
                  <span className={index === 2 ? 'w-full rounded-t bg-strong' : 'w-full rounded-t bg-alternative group-hover:bg-strong-alternative'} style={{
                height: `${height}px`
              }} />
                  <span className="text-caption-md-medium text-assistive">
                    {index + 1}
                  </span>
                </button>)}
            </div>
          </Section.Container>
        </div>

        <aside className="border-l border-alternative pl-xl">
          <p className="text-caption-md-semibold text-primary">
            {focusTitle(focus)}
          </p>
          <h2 className="text-heading-md-semibold mt-xs text-default">
            {focusDescription(focus)}
          </h2>

          <div className="mt-xl divide-y divide-alternative border-y border-alternative">
            {['Alex Kim', 'Jisoo Lee', 'Minho Park', 'Sora Choi'].map((name, index) => <button key={name} type="button" onClick={() => setActionComplete(false)} className="flex w-full items-center justify-between py-md text-left hover:text-primary">
                  <span className="text-body-md-semibold text-default">
                    {name}
                  </span>
                  <span className="text-body-sm-medium text-assistive">
                    {index + 1}/5
                  </span>
                </button>)}
          </div>

          <Button intent="strong" size="lg" className="mt-xl w-full" onClick={() => setActionComplete(true)}>
            {t('lms:lms.studentStats.learningStatus')}
          </Button>

          {actionComplete && <p className="mt-md border-l-2 border-positive pl-sm text-body-sm-medium text-positive">
              {t('lms:lms.questionStats.allResponses.title')}
            </p>}
        </aside>
      </div>
    </>;
  if (standalone) {
    return <main className="min-h-screen bg-neutral px-3xl py-3xl">
        <div className="mx-auto flex w-[min(100%,1120px)] flex-col gap-xl">
          {content}
        </div>
      </main>;
  }
  return <Layout data-sentry-element="Layout" data-sentry-component="TeacherDashboardPrototypePageView" data-sentry-source-file="TeacherDashboardPrototypePageView.tsx">{content}</Layout>;
}